/**
 * TanStack Query hooks for the admin curriculum lifecycle endpoints.
 *
 * - `useCurriculumHealth` polls `/admin/curriculum/health` while a job is
 *   running so the page updates live without manual refresh.
 * - `useResetCurriculum` / `useRetryCurriculumEmbeddings` trigger the
 *   destructive + retry jobs. The UI does its own confirmation handling.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import type { CurriculumHealthResponse, CurriculumJobTriggerResponse } from '@formency/shared';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';

// ─── Query keys ─────────────────────────────────────────────────────────────

export const adminCurriculumKeys = {
  all: ['f', 'admin-curriculum'] as const,
  health: () => [...adminCurriculumKeys.all, 'health'] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * Curriculum health snapshot. While a curriculum_* job is running the UI
 * passes a short `refetchInterval` so the progress bars update live. When
 * nothing's running the page can leave it at default (no polling).
 */
export function useCurriculumHealth(options?: { refetchInterval?: number | false }) {
  return useQuery<CurriculumHealthResponse>({
    queryKey: adminCurriculumKeys.health(),
    queryFn: () => api.get<CurriculumHealthResponse>('/admin/curriculum/health'),
    refetchInterval: options?.refetchInterval,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/**
 * Trigger destructive reset + seed + embed. The UI is responsible for
 * collecting the typed confirmation string and passing it through.
 */
export function useResetCurriculum() {
  const qc = useQueryClient();
  return useMutation<CurriculumJobTriggerResponse, unknown, { confirmation: string }>({
    mutationFn: (body) =>
      api.post<CurriculumJobTriggerResponse>('/admin/curriculum/reset-and-seed', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminCurriculumKeys.health() });
      toast.success('Curriculum reset enqueued');
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Failed to trigger reset'));
    },
  });
}

/** Trigger embedding-only retry for any rows still NULL. */
export function useRetryCurriculumEmbeddings() {
  const qc = useQueryClient();
  return useMutation<CurriculumJobTriggerResponse, unknown, void>({
    mutationFn: () =>
      api.post<CurriculumJobTriggerResponse>('/admin/curriculum/retry-embeddings', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminCurriculumKeys.health() });
      toast.success('Embedding retry enqueued');
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Failed to trigger retry'));
    },
  });
}
